/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { Save } from 'lucide-react';
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { useDisclosure } from '@chakra-ui/react';
import { useEffect, useState, useCallback } from 'react';

import { lang } from '@lang';
import { saveQuote, saveCustomerBlueprint, VerifiedQuoteItem, VerifyQuoteItemsSummary } from '@external';
import { useAppToast, useDesign } from '@hooks';
import { customerState, projectState, selectNavigation, useAppDispatch, useAppSelector } from '@state';
import { Feature, FeatureHoookParams } from '..';

import { repointDetachedDraft } from '@features/customer/helpers';
import { useShareLinkFeature } from '@features/social';
import { QuoteVerificationModal } from '@components/modals';
import { useQuoteVerification } from './use-quote-verification.hook';
import { validateCrmOperation } from './validate-crm-operation';

type SaveQuoteFeatureParams = FeatureHoookParams;

export type SaveQuoteFeatureValue = {
	isVerificationOpen: boolean;
	isVerifying: boolean;
	isSubmitting: boolean;
	verifiedItems: VerifiedQuoteItem[];
	summary: VerifyQuoteItemsSummary | null;
	openVerification: () => void;
	closeVerification: () => void;
	confirmQuote: () => void;
	cancelQuote: () => void;
	VerificationModal: React.ReactNode;
};

export const useSaveQuoteFeature = (_params: SaveQuoteFeatureParams = {}): Feature<SaveQuoteFeatureValue> => {
	const { getToken } = useKeystoneSession();
	const [controller, design] = useDesign();
	const dispatch = useAppDispatch();

	const customer = useAppSelector(customerState);
	const project = useAppSelector(projectState);
	const appNav = useAppSelector(selectNavigation);
	const link = useShareLinkFeature().value;

	const appToast = useAppToast();

	const [quoteId, setQuoteId] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [quoteName, setQuoteName] = useState<string>('');
	const [publishQuote, setPublishQuote] = useState<boolean>(false);
	const [step, setStep] = useState<'deal-selection' | 'verification'>('deal-selection');
	const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

	const { isOpen: isVerificationOpen, onOpen: openVerification, onClose: closeVerification } = useDisclosure();
	const {
		isVerifying,
		verifiedItems,
		summary,
		verifyItems,
		handlePriceSelectionChange,
		resetVerification,
		buildPriceOverrides
	} = useQuoteVerification();

	const dealId = selectedDealId;
	const msrpData = controller?.kitchen?.itemizedMsrp;

	useEffect(() => {
		if (controller && quoteId !== null) {
			controller.kitchen.quoteId = quoteId;
		}
	}, [quoteId, controller]);

	// Error state is consumed directly by handleConfirmQuote — no separate toast needed

	const [disabled, tooltip] = (() => {
		if (customer.email === '') return [true, lang.document.tooltip.missingCustomer];
		if (project.length === 0) return [true, lang.document.tooltip.missingHubspotData];
		if (!msrpData || msrpData.subtotal === 0) return [true, lang.crm.tooltip.noQuoteData];
		return [false, lang.crm.tooltip.generateQuote];
	})();

	const saveAsNewVersion = async (): Promise<void> => {
		if (!controller) return;
		try {
			const kitchens = controller.kitchen.context.blueprint?.kitchens;
			if (kitchens) {
				const draft = kitchens.findVersion(null);
				if (draft) kitchens.promoteDraft();
				else kitchens.newVersionFromLatest();
			}
			await saveCustomerBlueprint({
				controller,
				customerEmail: appNav.email ?? customer.email,
				mode: 'live',
				getToken,
				setSuccess: () => appToast.success({ title: lang.customer.message.saveSuccess }),
				setError: (err) => console.error('Version save error (non-blocking):', err)
			});
		} catch (versionErr) {
			console.error('Version save failed (non-blocking):', versionErr);
		} finally {
			repointDetachedDraft(design);
		}
	};

	const defaultQuoteName = appNav.designId || `${customer.firstName} ${customer.lastName}`.trim();

	const startVerification = useCallback(async (): Promise<void> => {
		setStep('verification');
		const msrp = controller?.kitchen?.itemizedMsrp;
		const promotions = controller?.kitchen?.promotionalSkus?.children ?? null;
		if (msrp) {
			await verifyItems({
				islands: msrp.islands ?? null,
				countertops: msrp.countertops ?? [],
				backsplashes: msrp.backsplashes ?? [],
				items: msrp.items ?? [],
				accessories: msrp.accessories ?? []
			}, promotions);
		}
	}, [verifyItems, controller]);

	const handleOpenVerification = useCallback(async (): Promise<void> => {
		setQuoteName(defaultQuoteName);
		openVerification();

		// Pre-populate deal from kitchen.dealId if it matches a project deal
		const kitchenDealId = controller?.kitchen?.dealId;
		const preSelectedDeal = kitchenDealId ? project.find((d) => d.dealId === kitchenDealId) : null;

		if (preSelectedDeal) {
			setSelectedDealId(preSelectedDeal.dealId);
			await startVerification();
		} else if (project.length === 1) {
			setSelectedDealId(project[0].dealId);
			await startVerification();
		} else {
			setStep('deal-selection');
			setSelectedDealId(null);
		}
	}, [openVerification, startVerification, project, defaultQuoteName, controller]);

	const handleQuoteNameChange = useCallback((name: string): void => {
		setQuoteName(name);
	}, []);

	const handlePublishQuoteChange = useCallback((value: boolean): void => {
		setPublishQuote(value);
	}, []);

	const handleConfirmQuote = useCallback(async (): Promise<void> => {
		if (!controller || !dealId) return;

		// Validate required CRM fields before proceeding
		const validation = validateCrmOperation({ customer, project, selectedDealId: dealId });
		if (!validation.canProceed) {
			appToast.error({
				title: 'Cannot save quote',
				description: validation.blockingErrors.join('\n')
			});
			return;
		}

		setIsSubmitting(true);
		closeVerification();

		// Show warnings if any, but proceed
		if (validation.warningMessage) {
			appToast.warning({
				title: 'Quote will be saved with missing fields',
				description: validation.warningMessage
			});
		}

		const toastCtrl = appToast.loading({ title: 'Saving quote...' });

		const selectedDeal = project.find((d) => d.dealId === dealId);
		const [quoteResult, quoteError] = await saveQuote({
			controller,
			customer,
			dealAddress: selectedDeal ? { address: selectedDeal.address, city: selectedDeal.city, state: selectedDeal.state, zip: selectedDeal.zip } : undefined,
			dealId,
			link,
			getToken,
			setQuoteId,
			setError,
			priceOverrides: buildPriceOverrides(),
			quoteName: quoteName || defaultQuoteName,
			publish: publishQuote
		});

		if (quoteResult && !quoteError) {
			await saveAsNewVersion();
			setIsSubmitting(false);
			toastCtrl.success({ title: `${lang.crm.message.quoteCreated} ${dealId}`, description: lang.crm.message.quoteLineItemsAutomated, persistent: true });
		} else {
			setIsSubmitting(false);
			toastCtrl.error({ title: 'Error creating quote', description: quoteError || undefined });
		}
	}, [controller, customer, dealId, link, getToken, appToast, closeVerification, error, buildPriceOverrides, dispatch, quoteName, defaultQuoteName, publishQuote]);

	const handleCancelQuote = useCallback((): void => {
		closeVerification();
		resetVerification();
		setStep('deal-selection');
		setSelectedDealId(null);
	}, [closeVerification, resetVerification]);

	const VerificationModal = (
		<QuoteVerificationModal
			isOpen={isVerificationOpen}
			onClose={handleCancelQuote}
			isLoading={isVerifying}
			verifiedItems={verifiedItems}
			summary={summary}
			onConfirm={handleConfirmQuote}
			onCancel={handleCancelQuote}
			isSubmitting={isSubmitting}
			onPriceSelectionChange={handlePriceSelectionChange}
			quoteName={quoteName}
			onQuoteNameChange={handleQuoteNameChange}
			publishQuote={publishQuote}
			onPublishQuoteChange={handlePublishQuoteChange}
			step={step}
			deals={project}
			selectedDealId={selectedDealId}
			onDealSelect={setSelectedDealId}
			onContinueToverification={startVerification}
		/>
	);

	return {
		name: 'saveQuote',
		label: lang.crm.label.saveQuote,
		shortcut: '',
		tooltip,
		icon: (props = {}) => <Save {...props} />,
		disabled,
		value: {
			isVerificationOpen,
			isVerifying,
			isSubmitting,
			verifiedItems,
			summary,
			openVerification: handleOpenVerification,
			closeVerification,
			confirmQuote: handleConfirmQuote,
			cancelQuote: handleCancelQuote,
			VerificationModal
		},
		hidden: false,
		handler: async () => {
			await handleOpenVerification();
		}
	};
};
